<template>
  <Teleport to="body">
    <div v-if="show" class="gl-je-details-modal-overlay">
      <div
        class="gl-je-details-modal-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="gl-je-details-title"
        @click.stop
      >
        <header class="gl-je-details-modal-header">
          <div class="gl-je-details-modal-heading">
            <h2 id="gl-je-details-title" class="gl-je-details-modal-title">
              Journal Entry <strong v-if="entryNoTrim">{{ entryNoTrim }}</strong>
            </h2>
            <div v-if="entryMeta.length" class="gl-je-details-modal-meta">
              <div
                v-for="(chip, idx) in entryMeta"
                :key="'je-meta-' + idx"
                class="gl-je-details-modal-chip"
              >
                <span class="gl-je-details-modal-chip-k">{{ chip.label }}</span>
                <span class="gl-je-details-modal-chip-v">{{ chip.value }}</span>
              </div>
            </div>
          </div>
          <button
            type="button"
            class="gl-je-details-modal-close"
            aria-label="Close"
            title="Close"
            @click="handleClose"
          >
            <i class="bi bi-x-lg" aria-hidden="true"></i>
          </button>
        </header>

        <div v-if="hasJournalTabs" class="gl-je-details-modal-tabs">
          <TabNavigation v-model="activeJournalTabId" :tabs="journalTabs" />
        </div>

        <div class="gl-je-details-modal-body">
          <div v-if="loading" class="gl-je-details-modal-empty">Loading journal entry…</div>
          <div v-else-if="error" class="gl-je-details-modal-empty gl-je-details-modal-empty--error">
            <p>{{ error }}</p>
            <button type="button" class="gl-je-details-modal-retry" @click="loadEntry">Retry</button>
          </div>
          <template v-else>
            <p v-if="entryComments" class="gl-je-details-modal-comments">{{ entryComments }}</p>
            <table v-if="flattenedRows.length" class="gl-je-details-modal-table">
              <thead>
                <tr>
                  <th>Account</th>
                  <th>Memo</th>
                  <th class="gl-je-details-modal-num">Debit</th>
                  <th class="gl-je-details-modal-num">Credit</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in flattenedRows" :key="row.rowKey">
                  <td>{{ row.accountName }}</td>
                  <td>{{ row.memo }}</td>
                  <td class="gl-je-details-modal-num">{{ formatAmount(row.debit) }}</td>
                  <td class="gl-je-details-modal-num">{{ formatAmount(row.credit) }}</td>
                </tr>
              </tbody>
              <tfoot>
                <tr>
                  <td colspan="2"><strong>Totals</strong></td>
                  <td class="gl-je-details-modal-num"><strong>{{ formatAmount(totalDebit) }}</strong></td>
                  <td class="gl-je-details-modal-num"><strong>{{ formatAmount(totalCredit) }}</strong></td>
                </tr>
              </tfoot>
            </table>
            <div v-else class="gl-je-details-modal-empty">No journal entry lines found.</div>
          </template>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script>
import './JournalEntryDetailsModal.css';
import JournalEntryService from '@/services/journalEntry.service.js';
import TabNavigation from '@/components/TabNavigation.vue';
import logger from '@/utils/logger';

export default {
  name: 'JournalEntryDetailsModal',
  components: {
    TabNavigation
  },
  props: {
    show: { type: Boolean, default: false },
    journalEntryId: { type: [String, Number], default: null },
    entryNumber: { type: String, default: '' },
    journalTabs: {
      type: Array,
      default: () => []
    }
  },
  emits: ['close'],
  data() {
    return {
      loading: false,
      error: '',
      entry: null,
      loadedKey: null,
      activeJournalTabId: null
    };
  },
  computed: {
    hasJournalTabs() {
      return Array.isArray(this.journalTabs) && this.journalTabs.length > 1;
    },
    activeJournalTab() {
      if (this.hasJournalTabs) {
        return this.journalTabs.find((tab) => tab.id === this.activeJournalTabId) || this.journalTabs[0];
      }

      return {
        journalEntryId: this.journalEntryId,
        entryNumber: this.entryNumber
      };
    },
    activeJournalEntryId() {
      return this.activeJournalTab?.journalEntryId ?? this.journalEntryId;
    },
    entryNoTrim() {
      return (this.entry?.entryNo || this.activeJournalTab?.entryNumber || this.entryNumber || '').trim();
    },
    entryComments() {
      const c = this.entry?.comments;
      return c && String(c).trim() ? String(c).trim() : '';
    },
    entryMeta() {
      const e = this.entry;
      if (!e) return [];
      const chips = [];
      if (e.entryDate) {
        chips.push({ label: 'Date', value: this.formatDate(e.entryDate) });
      }
      if (e.status) {
        chips.push({ label: 'Status', value: this.formatStatus(e.status) });
      }
      return chips;
    },
    flattenedRows() {
      const entryLines = Array.isArray(this.entry?.entryLines) ? this.entry.entryLines : [];
      return entryLines.map((line, index) => ({
        rowKey: `ln-${line.id ?? index}`,
        accountName: line.account?.accountName || '—',
        memo: line.memo && String(line.memo).trim() ? line.memo : '—',
        debit: line.debit,
        credit: line.credit
      }));
    },
    totalDebit() {
      return this.flattenedRows.reduce((s, r) => s + (Number(r.debit) || 0), 0);
    },
    totalCredit() {
      return this.flattenedRows.reduce((s, r) => s + (Number(r.credit) || 0), 0);
    }
  },
  watch: {
    show(val) {
      if (val) {
        this.resetActiveJournalTab();
        this.loadEntry();
      } else {
        this.entry = null;
        this.error = '';
        this.loadedKey = null;
        this.activeJournalTabId = null;
      }
    },
    journalEntryId() {
      if (this.show && !this.hasJournalTabs) {
        this.loadedKey = null;
        this.loadEntry();
      }
    },
    activeJournalTabId() {
      if (this.show && this.hasJournalTabs) {
        this.loadedKey = null;
        this.loadEntry();
      }
    },
    journalTabs: {
      deep: true,
      handler() {
        if (this.show) {
          this.resetActiveJournalTab();
          this.loadedKey = null;
          this.loadEntry();
        }
      }
    }
  },
  methods: {
    resetActiveJournalTab() {
      this.activeJournalTabId = this.journalTabs[0]?.id || null;
    },
    handleClose() {
      this.$emit('close');
    },
    formatDate(dateString) {
      if (!dateString) return '—';
      const date = new Date(dateString);
      if (Number.isNaN(date.getTime())) return '—';
      return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
    },
    formatStatus(status) {
      if (!status) return '—';
      return String(status)
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase());
    },
    formatAmount(amount) {
      if (amount === undefined || amount === null || amount === '') return '—';
      const n = Number(amount);
      if (Number.isNaN(n) || n <= 0) return '—';
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);
    },
    async loadEntry() {
      const id = this.activeJournalEntryId;
      if (id === null || id === undefined || id === '') {
        this.error = 'Journal entry ID is missing.';
        return;
      }
      const key = String(id);
      if (this.loadedKey === key && this.entry && !this.error) {
        return;
      }
      this.loading = true;
      this.error = '';
      try {
        const res = await JournalEntryService.getJournalEntryById(id);
        this.entry = res?.data ?? res;
        this.loadedKey = key;
      } catch (e) {
        logger.error('JournalEntryDetailsModal: load failed', e);
        this.error =
          (e && e.response && e.response.data && e.response.data.message) ||
          e.message ||
          'Could not load journal entry.';
        this.entry = null;
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>
